import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import moment from 'moment'

import {
  Grid, Paper, Table, TableHead,
  TableRow, TableCell, TableBody,
  TablePagination, TableContainer,
  Tooltip, Typography, Button, Dialog,
  DialogContent, DialogTitle, IconButton
} from '@material-ui/core'

import CloseIcon from '@material-ui/icons/Close'

import { getWeblogList } from '../../../../redux/actions'

import { makeStyles, withStyles } from '@material-ui/core/styles'

import { keyify2 } from '../../../../utils'

import TreeView from '@material-ui/lab/TreeView'
import UnitTree from '../../tools/UnitTree'

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    }
  }
}))(TableRow)

const useStyles = makeStyles((theme) => ({
  backBtn: {
    backgroundColor: '#8BB84B',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#588518'
    }
  },
  deleteBtn: {
    backgroundColor: theme.palette.action.active,
    margin: 'auto',
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.error.light
    }
  },
  greenBtn: {
    color: '#fff',
    backgroundColor: '#A4D164',
    '&:hover': {
      backgroundColor: '#8AB651'
    },
    borderRadius: '50%'
  },
  blueBtn: {
    backgroundColor: '#3984f7',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#0651C4'
    }
  },
  blueBackground: {
    backgroundColor: '#007BFF',
    '&:hover': {
      backgroundColor: '#0651C4'
    }
  },
  greenBackground: {
    backgroundColor: '#97b557',
    '&:hover': {
      backgroundColor: '#0651C4'
    }
  },
  changeButton: {
    color: '#007bff',
    fontWeight: 'bold',
    '&:focus': {
      outline: 'none'
    }
  },
  greyColor: {
    color: '#0000008A'
  },
  editPaper: {
    backgroundColor: '#ffffff',
    maxHeight: '50em',
    overflow: 'scroll'
  },
  wordList: {
    width: '100%'
  },
  customText: {
    textTransform: 'none !important',
    marginTop: '1rem'
  },
  tableContainer: {
    height: 'auto'
  },
  tablePaper: {
    height: '70vh',
    overflow: 'scroll'
  },
  tableText: {
    fontWeight: 'bold',
    color: '#A4D164'
  },
  tableHeadText: {
    fontWeight: 'bold',
    color: '#007bff'
  },
  table: {
    height: 250
  },
  tooltip: {
    fontSize: '1rem'
  },
  dialogContent: {
    minWidth: '20em'
  },
  mainDialog: {
    alignItems: 'baseline'
  }
}))

/**
 * Returns weblog list forms
 */
const WeblogList = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  // const unitID = useSelector(state => state.auth.unitTree[0].key)
  const TreeData = useSelector(state => state.auth.unitTree)

  const [selectedKeys, setSelectedKeys] = useState(keyify2(TreeData))
  const [selectedKey, setSelectedKey] = useState(TreeData[0].key)

  const [success, setSuccess] = useState(false)

  const [weblogList, setWeblogList] = useState([])

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(15)
  const [totalCount, setTotalCount] = useState(0)

  const [open, setOpen] = useState(false)
  const [unitName, setUnitName] = useState(TreeData[0].title)

  const [currentTimestamp] = useState(moment.unix())

  // const emptyRows = rowsPerPage - Math.min(rowsPerPage, weblogList.length - page * rowsPerPage)

  // const offsetNumber = weblogList.length

  // takes weblog data pulled in and sets the webloglist and total depending on current data
  const setData = useCallback((fullData) => {
    const count = fullData.count
    const data = fullData.data
    if (weblogList.length === 0) {
      setWeblogList(data)
      setTotalCount(count)
    } else {
      if (data.length > 0) {
        if (weblogList[0].unitID !== data[0].unitID) {
          setWeblogList(data)
        } else {
          const tempWeblogList = [...weblogList]
          data.forEach(obj => {
            tempWeblogList.push(obj)
          })
          setWeblogList(tempWeblogList)
        }
      } else if (data.length === 0) {
        setWeblogList([])
      }
    }
    setSuccess(true)
  }, [weblogList])

  // runs dispatch to get more weblog data, then runs the setData function to handle what's returned
  useEffect(() => {
    (async () => {
      if (success === false && weblogList.length === 0) {
        const totalPerPage = rowsPerPage
        const offsetNumber = weblogList.length
        await dispatch(getWeblogList(selectedKey, currentTimestamp, offsetNumber, totalPerPage, setData))
      }
    }
    )()
  }, [dispatch, selectedKey, currentTimestamp, rowsPerPage, setData, success, weblogList.length])

  // if going to the next page and the the total is higher than the current array, pull more weblog data
  const handleChangePage = async (e, newPage) => {
    if (newPage > page) {
      const total = newPage * rowsPerPage
      if (weblogList.length <= total) {
        const totalPerPage = rowsPerPage
        const offsetNumber = weblogList.length
        await dispatch(getWeblogList(selectedKey, currentTimestamp, offsetNumber, totalPerPage, setData))
      }
    }
    setPage(newPage)
  }

  // updates the rows per page and pulls appropriate numer of weblog entries
  const handleChangeRowsPerPage = async (event) => {
    setPage(0)
    setRowsPerPage(event.target.value)
    if (weblogList.length <= event.target.value && weblogList.length !== totalCount) {
      const totalPerPage = rowsPerPage
      const offsetNumber = weblogList.length
      await dispatch(getWeblogList(selectedKey, currentTimestamp, offsetNumber, totalPerPage, setData))
    }
  }

  // changes the selected unit and resets data
  const handleNodeSelect = async (keySelected, title) => {
    if (keySelected !== selectedKey) {
      setSelectedKey(keySelected)
      setPage(0)
      setWeblogList([])
      setRowsPerPage(15)
      setSuccess(false)
      setOpen(false)
      setUnitName(title)
    }
  }

  const handleNodeAugment = async (key, children, isExpanded) => {
    if (isExpanded) {
      const tempSelected = [...selectedKeys]
      const keysToRemove = [key, ...keyify2(children)]

      const newSelected = tempSelected.filter((selectedKey) => keysToRemove.findIndex((removeKey) => removeKey === selectedKey) === -1)

      setSelectedKeys(newSelected)
    } else {
      const tempSelected = [...selectedKeys]
      const keysToAdd = [key, ...keyify2(children)]
      const addedKeys = []

      keysToAdd.forEach((addKey) => {
        if (tempSelected.findIndex((selectedKey) => selectedKey === addKey) === -1) {
          addedKeys.push(addKey)
        }
      })

      setSelectedKeys([...selectedKeys, ...addedKeys])
    }
  }

  const truncateText = (str, length = 15) => {
    return str.length > 15 ? str.substring(0, length) + '...' : str
  }

  return (
    <>
      <Grid item container direction='row'>
        <Grid item container direction='column' sm={12}>
          <Grid item container direction='row'>
            <Grid item container direction='column' sm={6}>
              <h2 className='mr-3 mb-4'>
                Weblogs
              </h2>
            </Grid>
          </Grid>
          <Dialog open={open} classes={{ scrollPaper: classes.mainDialog }}>
            <DialogTitle disableTypography>
              <Grid container direction='row' className='mx-auto w-100' justify='space-between' alignItems='center'>
                <Grid item>
                  <Typography variant='h6' className={classes.dialogTitle}>Available Units</Typography>
                </Grid>
                <Grid item>
                  <IconButton className={classes.closeButton} onClick={() => setOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </DialogTitle>
            <DialogContent className={`${classes.dialogContent} pl-1`}>
              <TreeView expanded={selectedKeys} className={useStyles().unitTree} disableSelection>
                <UnitTree
                  onNodeSelect={handleNodeSelect}
                  selectedKeys={[selectedKey]}
                  treeData={TreeData}
                  expandedKeys={selectedKeys}
                  onNodeAugment={handleNodeAugment}
                />
              </TreeView>
            </DialogContent>
          </Dialog>
          <Grid container item direction='row' spacing={2} className='mx-auto'>
            <Grid item xs={12}>
              <Paper elevation={2} className={classes.tablePaper}>
                <Grid container item direction='column' justify='flex-end' xs={12} className={classes.tableContainer}>
                  {
                    weblogList.length > 0
                      ? (
                        <>
                          <Grid container direction='column' className='px-3 w-100 pt-3 pb-0 mx-auto my-auto'>
                            <Typography variant='h6' style={{ marginTop: '1rem', marginLeft: '1rem', marginBottom: '0', fontWeight: 'bold', color: '#0000008A', fontSize: '2rem' }}>{unitName.replace('_', ' ')}</Typography>
                            <Grid container item xs={3} style={{ marginLeft: '1rem' }}>
                              <Button
                                variant='text'
                                size='small'
                                className='pl-0'
                                classes={{ root: classes.changeButton }}
                                onClick={() => setOpen(true)}
                              >
                                Change Unit
                              </Button>
                            </Grid>
                          </Grid>
                          <Grid
                            container
                            direction='column'
                            justify='center'
                            className='px-3 w-100 pt-1 pb-3 mx-auto my-auto'
                            spacing={2}
                          >
                            <Grid container justify='center' spacing={1}>
                              <TablePagination
                                component='div'
                                page={page}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={[100, 50, 15]}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                                onChangePage={handleChangePage}
                                color='secondary'
                              />
                            </Grid>
                            <TableContainer>
                              <Table className={classes.table} size='small' aria-label='users-table'>
                                <TableHead>
                                  <TableRow>
                                    <TableCell className={`${classes.tableHeadText} unselectable`}>User</TableCell>
                                    <TableCell className={`${classes.tableHeadText} unselectable`} align='right'>Workstation</TableCell>
                                    <TableCell className={`${classes.tableHeadText} unselectable`} align='right'>URL Visited</TableCell>
                                    <TableCell className={`${classes.tableHeadText} unselectable`} align='right'>Date/Time</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {weblogList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((weblog, index) => (
                                    <StyledTableRow key={index}>
                                      <Tooltip
                                        title={<Typography className={classes.tooltip}>{weblog.username}</Typography>}
                                        placement='top'
                                        arrow
                                        enterDelay={500}
                                        enterNextDelay={500}
                                      >
                                        <TableCell
                                          className={classes.tableText}
                                          component='th'
                                          scope='row'
                                          style={{ cursor: 'pointer' }}
                                          width='20%'
                                          // onClick={(e) => { e.preventDefault() }}
                                        >
                                          {truncateText(weblog.username)}
                                        </TableCell>
                                      </Tooltip>
                                      <TableCell
                                        className={`${classes.tableText} unselectable`}
                                        align='right'
                                      >
                                        {weblog.hostname}
                                      </TableCell>
                                      <TableCell
                                        className={`${classes.tableText} unselectable`}
                                        align='right'
                                      >
                                        {weblog.path}
                                      </TableCell>
                                      <TableCell
                                        className={`${classes.tableText} unselectable`}
                                        align='right'
                                      >
                                        {moment.unix(weblog.createdAt).format('MM/DD/YYYY LT')}
                                      </TableCell>
                                    </StyledTableRow>
                                  ))}
                                  {/* {emptyRows > 0 &&
                                    <TableRow>
                                      <TableCell />
                                    </TableRow>} */}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid container direction='column' className='px-3 w-100 pt-3 pb-0 mx-auto my-auto'>
                            <Typography variant='h6' style={{ marginTop: '1rem', marginLeft: '1rem', marginBottom: '0', fontWeight: 'bold', color: '#0000008A', fontSize: '2rem' }}>{unitName.replace('_', ' ')}</Typography>
                          </Grid>
                          <Grid
                            container
                            direction='column'
                            justify='center'
                            className='px-3 w-100 pt-1 pb-3 mx-auto my-auto'
                            spacing={2}
                          >
                            <Grid container justify='center' align='center' spacing={1} className='mt-5'>
                              <Paper elevation={2} className='px-3 w-75 py-3 mx-auto my-auto'>
                                <Typography variant='h5' style={{ color: '#0000008A', fontWeight: 'bold' }}>{`No weblogs currently available for ${unitName.replace('_', ' ')}.`}</Typography>
                                <Typography variant='h6' style={{ color: '#0000008A' }}>Please select another unit.</Typography>
                                <Grid container item xs={3} style={{ marginLeft: '1rem' }}>
                                  <Button
                                    variant='text'
                                    size='small'
                                    className='pl-0'
                                    classes={{ root: classes.changeButton }}
                                    onClick={() => setOpen(true)}
                                  >
                                    Change Unit
                                  </Button>
                                </Grid>
                              </Paper>
                            </Grid>
                          </Grid>
                        </>
                      )
                  }
                </Grid>
              </Paper>
            </Grid>
          </Grid>

        </Grid>
      </Grid>
    </>
  )
}

export default WeblogList
